import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { timeAgo, initials } from "@/lib/format";
import { Timer, CheckCircle, UserCircle, Headset, X, Airplane } from "@phosphor-icons/react";

const Stat = ({ label, value, sub, icon: Icon, color, testId, to }) => {
  const inner = (
    <div className="hairline rounded-lg p-4 bg-white transition hover:border-[#4f4c90]/40 hover:shadow-sm h-full" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</div>
        <Icon size={18} weight="duotone" color={color} />
      </div>
      <div className="mt-2 font-heading text-3xl font-bold">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [assigned, setAssigned] = useState([]);
  const [currentSupport, setCurrentSupport] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("hd_support_banner_dismissed") === "1"; } catch { return false; }
  });
  const [mineStats, setMineStats] = useState(null);
  const [health, setHealth] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data));
    api.get("/me/assigned").then((r) => setAssigned(r.data));
    api.get("/schedule/current").then((r) => setCurrentSupport(r.data)).catch(() => {});
    api.get("/availability").then((r) => setAvailability(r.data)).catch(() => {});
    api.get("/tickets/mine/stats").then((r) => setMineStats(r.data)).catch(() => {});
    api.get("/stats/system-health").then((r) => setHealth(r.data)).catch(() => {});
  }, []);

  const dismissBanner = () => {
    setDismissed(true);
    try { sessionStorage.setItem("hd_support_banner_dismissed", "1"); } catch (_) { /* noop */ }
  };

  if (!stats) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {health && !health.ok && (
        <div
          data-testid="ingestion-health-banner"
          className="rounded-lg border border-red-300 bg-red-50 p-4 flex items-start gap-3"
        >
          <div className="w-8 h-8 rounded-full bg-red-100 border border-red-300 flex items-center justify-center text-red-600 font-bold flex-shrink-0">!</div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-red-900 text-sm">
              {health.last_sync_error ? "Email ingestion is failing" : "Email ingestion is stale"}
            </div>
            <div className="text-[13px] text-red-800/90 mt-0.5">
              {health.last_sync_error ? (
                <>Last error: <span className="font-mono text-[11px] break-all">{health.last_sync_error}</span></>
              ) : (
                <>No successful poll since <span className="font-mono text-[11px]">{health.last_sync_at || "never"}</span>. Poller should run every 2 minutes.</>
              )}
            </div>
            <div className="text-[11px] text-red-800/70 font-mono mt-1">
              Check Settings → Microsoft Graph for details, and Entra client secret expiry if this persists.
            </div>
          </div>
        </div>
      )}
      {!dismissed && currentSupport?.active && currentSupport?.assignee && (
        <div className="hairline rounded-lg px-4 py-3 bg-[#39A36E]/10 border-[#39A36E]/30 flex items-center gap-3" data-testid="dashboard-support-banner">
          <Headset size={20} weight="duotone" className="text-[#39A36E] flex-shrink-0" />
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-xs font-medium border border-[#39A36E]/40">
            {initials(currentSupport.assignee.name)}
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-medium text-[#265b41]">{currentSupport.assignee.name}</span>
            <span className="text-slate-600"> is on support this {currentSupport.period === "AM" ? "morning" : "afternoon"}</span>
            <Link to="/admin/schedule" className="ml-2 text-[11px] text-[#39A36E] hover:underline">View rota →</Link>
          </div>
          <button onClick={dismissBanner} className="p-1 text-slate-500 hover:text-slate-800 rounded" data-testid="dismiss-support-banner" title="Dismiss">
            <X size={14} />
          </button>
        </div>
      )}
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Your day at a glance. Full reporting lives on <Link to="/insights" className="text-[#4f4c90] hover:underline">Insights</Link>.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Open" value={stats.open_tickets} icon={Timer} color="#60A5FA" to="/tickets?open_only=1" />
        <Stat
          label="Assigned to me"
          value={assigned.length}
          sub={assigned.length ? "open tickets" : "you're clear"}
          icon={UserCircle}
          color="#4f4c90"
          to={user?.id ? `/tickets?assignee=${user.id}&open_only=1` : "/tickets"}
        />
        <Stat
          label="My avg resolution"
          value={stats.my_resolved_count ? `${stats.my_avg_resolution_hours}h` : "—"}
          sub={
            stats.my_resolved_count
              ? (stats.my_resolved_total && stats.my_resolved_total !== stats.my_resolved_count
                  ? `based on ${stats.my_resolved_count} of ${stats.my_resolved_total}`
                  : `${stats.my_resolved_count} resolved`)
              : "no data yet"
          }
          icon={CheckCircle}
          color="#4f4c90"
          testId="my-avg-resolution"
          to={user?.id ? `/tickets?assignee=${user.id}&status=resolved` : "/tickets?status=resolved"}
        />
        <Stat
          label="Tickets resolved this week"
          value={mineStats ? mineStats.resolved_this_week : "—"}
          sub={mineStats ? `${mineStats.resolved_total} all-time` : "loading…"}
          icon={CheckCircle}
          color="#39A36E"
          testId="my-tickets-done"
          to={user?.id ? `/tickets?assignee=${user.id}&status=resolved` : "/tickets?status=resolved"}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Stat
          label="Average Business Systems Resolution Time"
          value={`${(stats.avg_resolution_hours_by_team || {}).systems ?? 0}h`}
          sub={`${(stats.resolved_count_by_team || {}).systems ?? 0} resolved`}
          icon={CheckCircle}
          color="#4f4c90"
          testId="team-avg-systems"
          to="/tickets?teams=systems&status=resolved"
        />
        <Stat
          label="Average IT Resolution Time"
          value={`${(stats.avg_resolution_hours_by_team || {}).it ?? 0}h`}
          sub={`${(stats.resolved_count_by_team || {}).it ?? 0} resolved`}
          icon={CheckCircle}
          color="#39A36E"
          testId="team-avg-it"
          to="/tickets?teams=it&status=resolved"
        />
      </div>

      <div className="hairline rounded-lg p-4 bg-white" data-testid="assigned-to-me-widget">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading font-semibold flex items-center gap-2">
              <UserCircle size={16} weight="duotone" className="text-[#4f4c90]" />
              Assigned to me
            </div>
            <div className="text-xs text-slate-500">{assigned.length} open ticket{assigned.length === 1 ? "" : "s"} — {user?.name}</div>
          </div>
          <Link
            to={user?.id ? `/tickets?assignee=${user.id}&open_only=1` : "/tickets?open_only=1"}
            className="text-xs text-[#8B87C8] hover:text-slate-900"
            data-testid="assigned-to-me-view-all"
          >View all →</Link>
        </div>
        {assigned.length === 0 ? (
          <div className="text-slate-500 text-sm py-6 text-center">You have no open tickets. Nice.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {assigned.slice(0, 5).map((t) => (
              <Link key={t.id} to={`/tickets/${t.id}`} data-testid="assigned-ticket-row"
                className="flex items-center gap-3 py-2 hover:bg-slate-50 -mx-2 px-2 rounded">
                <span className="font-mono text-xs text-slate-500 w-10">#{t.number}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{t.subject}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{timeAgo(t.created_at)} · {t.requester_name}</div>
                </div>
                <span className={`chip status-${t.status}`}>{t.status.replace("_"," ")}</span>
                <span className={`chip prio-${t.priority}`}>{t.priority}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {availability?.connected && (() => {
        // "Leave approaching" heads-up — anyone whose leave starts in the next 7 days.
        const soon = (availability.items || [])
          .map((p) => p.upcoming_leave ? { ...p.upcoming_leave, person: p } : null)
          .filter(Boolean)
          .filter((x) => {
            const days = Math.round((new Date(x.start_date) - new Date()) / 86400000);
            return days >= 0 && days <= 7;
          })
          .sort((a, b) => a.start_date.localeCompare(b.start_date));
        if (soon.length === 0) return null;
        return (
          <div className="hairline rounded-lg p-3 bg-amber-50 border-amber-200 flex items-start gap-3" data-testid="upcoming-leave-heads-up">
            <Airplane size={18} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium text-amber-900">Heads up — leave approaching</div>
              <div className="text-xs text-amber-900/80 mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                {soon.slice(0, 6).map((x, i) => {
                  const s = new Date(x.start_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
                  const e = new Date(x.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                  return <span key={i}><b>{x.person.full_name}</b> · {x.category} · {s}{x.start_date !== x.end_date ? ` → ${e}` : ""}</span>;
                })}
                {soon.length > 6 && <Link to="/team" className="text-amber-800 underline">+{soon.length - 6} more</Link>}
              </div>
            </div>
          </div>
        );
      })()}

      {availability?.connected && (
        <div className="hairline rounded-lg p-4 bg-white" data-testid="team-availability-widget">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-heading font-semibold flex items-center gap-2">
                <Airplane size={16} weight="duotone" className="text-[#4f4c90]" />
                On leave today
              </div>
              <div className="text-xs text-slate-500">
                {(() => {
                  const onLeave = (availability.items || []).filter((x) => x.status === "on_leave");
                  const total = (availability.items || []).length;
                  return `${onLeave.length} of ${total} team members`;
                })()}
              </div>
            </div>
            <Link to="/team" className="text-xs text-[#8B87C8] hover:text-slate-900" data-testid="team-widget-view-all">View team →</Link>
          </div>
          {(() => {
            const onLeave = (availability.items || []).filter((x) => x.status === "on_leave");
            if (onLeave.length === 0) return <div className="text-slate-500 text-sm py-4 text-center">Everyone&apos;s in today.</div>;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {onLeave.slice(0, 12).map((p) => {
                  const back = p.leave_to ? new Date(p.leave_to).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";
                  return (
                    <div key={p.employee_id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50" data-testid="team-widget-person">
                      <div className="w-7 h-7 rounded-full bg-[#4f4c90]/15 border border-[#4f4c90]/30 flex items-center justify-center text-[10px] font-medium text-[#4f4c90] flex-shrink-0">
                        {(p.full_name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{p.full_name}</div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{p.leave_category} · back {back}</div>
                      </div>
                    </div>
                  );
                })}
                {onLeave.length > 12 && (
                  <Link to="/team" className="text-xs text-[#8B87C8] hover:text-slate-900 flex items-center justify-center py-1.5">+{onLeave.length - 12} more</Link>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
